import { useState } from 'react'
import Button from '../Button/Button'
import style from './phone-verification.module.scss'

export interface OTPVerificationProps {
  email: string
  isLoading: boolean
  onVerify: (otp: string) => Promise<void>
  onResend: () => Promise<void>
  onBack: () => void
  backButtonText?: string
}

/**
 * Shared OTP Verification component
 * Used by both Login and Signup flows to avoid duplication
 */
export const OTPVerification = ({
  email,
  isLoading,
  onVerify,
  onResend,
  onBack,
  backButtonText = 'Change Email',
}: OTPVerificationProps) => {
  const [otp, setOtp] = useState<string>('')
  const [error, setError] = useState<string>('')

  const validateOTP = (): boolean => {
    if (!otp) {
      setError('OTP is required')
      return false
    } else if (otp.length !== 6) {
      setError('OTP must be exactly 6 digits')
      return false
    } else if (!/^\d{6}$/.test(otp)) {
      setError('OTP must contain only numbers')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateOTP()) {
      return
    }

    await onVerify(otp)
  }

  const handleResend = async () => {
    setOtp('')
    setError('')
    await onResend()
  }

  const handleBack = () => {
    setOtp('')
    setError('')
    onBack()
  }

  return (
    <form className={style.form} onSubmit={handleSubmit}>
      <p className={style.description}>
        Enter the 6-digit code sent to <b>{email}</b>
      </p>

      <div className={style.emailInputContainer}>
        <input
          type="text"
          className={style.emailInput}
          placeholder="Enter 6-digit OTP"
          value={otp}
          maxLength={6}
          onChange={e => {
            const value = e.target.value.replace(/\D/g, '')
            setOtp(value)
            setError('')
          }}
        />
        {error && <span className={style.errorMessage}>{error}</span>}
      </div>

      <div className={style.buttonGroup}>
        <Button
          label={isLoading ? 'Verifying...' : 'Verify'}
          type="submit"
          fullWidth={true}
          disabled={isLoading}
        />
        <button
          type="button"
          className={style.linkButton}
          onClick={handleResend}
          disabled={isLoading}
        >
          Resend OTP
        </button>
        <button type="button" className={style.linkButton} onClick={handleBack}>
          {backButtonText}
        </button>
      </div>
    </form>
  )
}

export default OTPVerification
